import { useEffect, useState } from "react"

import { Check } from "./RetryChecks"

// Kept for the tab's lifetime so a reload doesn't offer the retry button for a check that's still running
const STORAGE_KEY = "hades-queued-checks"

/** `${submissionId}:${check}` mapped to the time it was queued */
type Queued = Record<string, number>

const readQueued = (): Queued => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? "{}")
  } catch {
    return {}
  }
}

/** Checks the professor has re-queued, until hades reports them */
export const useQueuedChecks = (submissionId: string) => {
  const [queued, setQueued] = useState<Queued>(readQueued)

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(queued))
    } catch {
      // Storage can be unavailable (private mode): the state still works for this page
    }
  }, [queued])

  const key = (check: Check) => `${submissionId}:${check}`

  /** When the check was queued, or `undefined` if it isn't running */
  const queuedAt = (check: Check): number | undefined => queued[key(check)]

  const setCheckQueued = (checks: Check[], value: boolean) =>
    setQueued(q => {
      const next = { ...q }
      for (const check of checks) {
        if (value) next[key(check)] = Date.now()
        else delete next[key(check)]
      }
      return next
    })

  return {
    isQueued: (check: Check) => queuedAt(check) !== undefined,
    queuedAt,
    setCheckQueued,
  }
}
